// HIA.1 — Homologação controlada do Consultor IA.
//
// Server-fns para:
//  - resolver a coorte de um projeto (elegibilidade + snapshot inicial);
//  - registar desfecho de sugestões (applied/rejected/edited/regenerated);
//  - submeter feedback contextual do utilizador;
//  - fechar coorte com snapshot final;
//  - alimentar dashboard admin de homologação;
//  - avaliar limiares de sucesso/pausa.

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { getFullBusinessPlanBundle } from "@/lib/business-plan/editor.functions";

// ---------- constantes ----------

export const COHORT_GROUPS = ["internal", "assisted", "expanded", "gradual", "control"] as const;
export type CohortGroup = typeof COHORT_GROUPS[number];

const REJECT_REASONS = [
  "not_my_business","too_generic","too_long","too_short",
  "incorrect_info","did_not_understand","prefer_write_myself","other",
] as const;

const FEEDBACK_TRIGGERS = [
  "first_applied","third_use","section_completed",
  "review_closed","repeat_regeneration","critical_conflict","exit_survey",
] as const;

const RATINGS = ["very_useful","useful","slightly_useful","not_useful","yes","partial","no"] as const;

// ---------- helpers ----------

function fnv1a(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return Math.abs(h);
}

async function assertAdmin(supabase: any, userId: string) {
  const { data } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
  if (!data) throw new Error("Somente administradores.");
}

async function isFlagOn(supabase: any, key: string): Promise<{ enabled: boolean; rollout: number; targets: string[] }> {
  const { data } = await supabase
    .from("feature_flags")
    .select("enabled, rollout_pct, target_user_ids")
    .eq("key", key)
    .maybeSingle();
  return {
    enabled: !!data?.enabled,
    rollout: Number(data?.rollout_pct ?? 0),
    targets: (data?.target_user_ids as string[] | null) ?? [],
  };
}

async function loadPlanSnapshot(supabase: any, userId: string): Promise<{
  completeness: number | null;
  consistency: number | null;
  sectionsComplete: number;
  sectionsEmpty: number;
  pending: number;
  projectId: string | null;
  methodology: string | null;
} | null> {
  // Reusa o bundle: tem projeto ativo, plano e pendências.
  const bundle: any = await (getFullBusinessPlanBundle as any)();
  if (!bundle?.hasProject || !bundle.fullPlan) return null;
  const p = bundle.fullPlan.progress;
  const completeness = p.total > 0 ? p.pct : null;
  return {
    completeness,
    consistency: null, // preenchido apenas se runAIReview correr durante a coorte
    sectionsComplete: p.complete,
    sectionsEmpty: p.empty,
    pending: bundle.fullPlan.pendingItems.length,
    projectId: bundle.bundle?.project?.id ?? null,
    methodology: bundle.fullPlan.methodology?.methodologyLabel ?? null,
  };
}

// ---------- resolveCohort ----------

const ResolveCohortInput = z.object({
  projectId: z.string().uuid(),
  methodology: z.string().max(120).nullable().optional(),
}).strict();

export type ResolveCohortResult = {
  eligible: boolean;
  reason: "flag_off" | "control" | "not_targeted" | "eligible" | "paused";
  group: CohortGroup | null;
  cohortId: string | null;
};

/** Núcleo reutilizável: pode ser chamado internamente por outras server-fns. */
export async function resolveHomologationCohortImpl(
  supabase: any,
  userId: string,
  data: { projectId: string; methodology?: string | null },
): Promise<ResolveCohortResult> {

    // Se já existe coorte activa, respeita o grupo (nunca mudar durante análise).
    const { data: existing } = await supabase
      .from("ai_homologation_cohorts")
      .select("id, cohort_group, status")
      .eq("user_id", userId)
      .eq("project_id", data.projectId)
      .maybeSingle();
    if (existing) {
      if (existing.status === "paused") {
        return { eligible: false, reason: "paused", group: existing.cohort_group as CohortGroup, cohortId: existing.id };
      }
      return {
        eligible: existing.cohort_group !== "control",
        reason: existing.cohort_group === "control" ? "control" : "eligible",
        group: existing.cohort_group as CohortGroup,
        cohortId: existing.id,
      };
    }

    const flag = await isFlagOn(supabase, "ai_homologation_enabled");
    if (!flag.enabled) {
      return { eligible: true, reason: "flag_off", group: null, cohortId: null };
    }

    // Grupo: admin/target → internal; senão bucket por rollout_pct → gradual, resto → control.
    const { data: role } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
    let group: CohortGroup = "control";
    if (role || flag.targets.includes(userId)) {
      group = "internal";
    } else {
      const bucket = fnv1a(`ai_homologation::${userId}`) % 100;
      if (bucket < flag.rollout) group = "gradual";
    }

    const snap = await loadPlanSnapshot(supabase, userId);

    const { data: inserted, error } = await supabase
      .from("ai_homologation_cohorts")
      .insert({
        user_id: userId,
        project_id: data.projectId,
        cohort_group: group,
        methodology: data.methodology ?? snap?.methodology ?? null,
        completeness_start: snap?.completeness ?? null,
        sections_complete_start: snap?.sectionsComplete ?? null,
        sections_empty_start: snap?.sectionsEmpty ?? null,
        pending_start: snap?.pending ?? null,
      })
      .select("id")
      .maybeSingle();
    if (error) throw error;

    try {
      await supabase.from("analytics_events").insert({
        user_id: userId,
        event_name: "ai_homologation_started",
        metadata: {
          project_id: data.projectId,
          group,
          methodology: data.methodology ?? snap?.methodology ?? null,
          completeness_start: snap?.completeness,
          sections_complete_start: snap?.sectionsComplete,
          sections_empty_start: snap?.sectionsEmpty,
          pending_start: snap?.pending,
        },
      });
    } catch { /* non-fatal */ }

    if (group === "control") {
      return { eligible: false, reason: "control", group, cohortId: inserted?.id ?? null };
    }
    return { eligible: true, reason: "eligible", group, cohortId: inserted?.id ?? null };
}

/** Wrapper server-fn: chamado do cliente. */
export const resolveHomologationCohort = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => ResolveCohortInput.parse(raw))
  .handler(async ({ data, context }): Promise<ResolveCohortResult> => {
    return resolveHomologationCohortImpl(context.supabase, context.userId, data);
  });

// ---------- recordSuggestionOutcome ----------

const OutcomeInput = z.object({
  suggestionId: z.string().uuid(),
  outcome: z.enum(["applied", "applied_after_edit", "rejected", "regenerated"]),
  editMagnitude: z.enum(["small", "moderate", "large"]).nullable().optional(),
  rejectReason: z.enum(REJECT_REASONS).nullable().optional(),
  comment: z.string().max(500).nullable().optional(),
  sectionKey: z.string().max(80).nullable().optional(),
  fieldKey: z.string().max(80).nullable().optional(),
  projectId: z.string().uuid().nullable().optional(),
}).strict();

export const recordSuggestionOutcome = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => OutcomeInput.parse(raw))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    // Registo do desfecho (sem texto da sugestão).
    await supabase.from("ai_suggestion_outcomes").insert({
      suggestion_id: data.suggestionId,
      user_id: userId,
      project_id: data.projectId ?? null,
      section_key: data.sectionKey ?? null,
      field_key: data.fieldKey ?? null,
      outcome: data.outcome,
      edit_magnitude: data.editMagnitude ?? null,
      reject_reason: data.rejectReason ?? null,
      comment: data.comment ?? null,
    });

    // Contadores agregados na coorte activa (se existir).
    if (data.projectId) {
      const map: Record<string, string> = {
        applied: "suggestions_applied",
        applied_after_edit: "suggestions_edited",
        rejected: "suggestions_rejected",
        regenerated: "suggestions_regenerated",
      };
      const col = map[data.outcome];
      if (col) {
        const { data: cohort } = await (supabase as any)
          .from("ai_homologation_cohorts")
          .select("*")
          .eq("user_id", userId).eq("project_id", data.projectId)
          .eq("status", "active")
          .maybeSingle();
        if (cohort) {
          const current = Number((cohort as any)[col] ?? 0);
          await (supabase as any)
            .from("ai_homologation_cohorts")
            .update({ [col]: current + 1 } as any)
            .eq("id", (cohort as any).id);
        }
      }
    }
    return { ok: true };
  });

// ---------- submitHomologationFeedback ----------

const FeedbackInput = z.object({
  trigger: z.enum(FEEDBACK_TRIGGERS),
  rating: z.enum(RATINGS),
  comment: z.string().max(1000).nullable().optional(),
  projectId: z.string().uuid().nullable().optional(),
  context: z.record(z.string(), z.any()).optional(),
}).strict();

export const submitHomologationFeedback = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => FeedbackInput.parse(raw))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const flag = await isFlagOn(supabase, "ai_homologation_feedback_enabled");
    if (!flag.enabled) return { ok: false, skipped: true };

    await supabase.from("ai_homologation_feedback").insert({
      user_id: userId,
      project_id: data.projectId ?? null,
      trigger: data.trigger,
      rating: data.rating,
      comment: data.comment ?? null,
      context: (data.context ?? {}) as never,
    });
    try {
      await supabase.from("analytics_events").insert({
        user_id: userId,
        event_name: "ai_homologation_feedback_submitted",
        metadata: { trigger: data.trigger, rating: data.rating, has_comment: !!data.comment },
      });
    } catch { /* noop */ }
    return { ok: true };
  });

// ---------- finishCohort ----------

const FinishInput = z.object({
  projectId: z.string().uuid(),
}).strict();

export const finishHomologationCohort = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => FinishInput.parse(raw))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: cohort } = await supabase
      .from("ai_homologation_cohorts")
      .select("id, entered_at, cohort_group")
      .eq("user_id", userId).eq("project_id", data.projectId)
      .eq("status", "active")
      .maybeSingle();
    if (!cohort) return { ok: false, notFound: true };

    const snap = await loadPlanSnapshot(supabase, userId);
    await supabase
      .from("ai_homologation_cohorts")
      .update({
        status: "finished",
        exited_at: new Date().toISOString(),
        completeness_end: snap?.completeness ?? null,
        sections_complete_end: snap?.sectionsComplete ?? null,
        sections_empty_end: snap?.sectionsEmpty ?? null,
        pending_end: snap?.pending ?? null,
      })
      .eq("id", cohort.id);
    try {
      await supabase.from("analytics_events").insert({
        user_id: userId,
        event_name: "ai_homologation_finished",
        metadata: {
          project_id: data.projectId,
          group: cohort.cohort_group,
          completeness_end: snap?.completeness,
          sections_complete_end: snap?.sectionsComplete,
          pending_end: snap?.pending,
        },
      });
    } catch { /* noop */ }
    return { ok: true };
  });

// ---------- getHomologationOverview (admin) ----------

export type HomologationOverview = {
  windowDays: number;
  cohorts: {
    total: number;
    byGroup: Record<CohortGroup, number>;
    finished: number;
    active: number;
    paused: number;
  };
  adoption: {
    eligibleUsers: number;
    openedUsers: number;
    messagedUsers: number;
    appliedUsers: number;
  };
  utility: {
    applied: number;
    appliedAfterEdit: number;
    rejected: number;
    regenerated: number;
    applyRate: number;
    rejectRate: number;
    usefulFeedbackRate: number;
  };
  outcome: {
    avgCompletenessDelta: number | null;
    avgPendingDelta: number | null;
    finishedCohorts: number;
  };
  operation: {
    llmErrors: number;
    llmTimeouts: number;
    costUsd: number;
    p95LatencyMs: number | null;
  };
  criteria: {
    success: Record<string, number>;
    pause: Record<string, number>;
  };
  decision: { state: "not_started" | "in_homologation" | "healthy" | "attention" | "blocked" | "ready_for_expansion"; missing: string[] };
  rejectReasons: Array<{ reason: string; count: number }>;
  feedbackByTrigger: Array<{ trigger: string; useful: number; notUseful: number }>;
  bySection: Array<{ section: string; suggestions: number; applied: number; rejected: number }>;
};

export const getHomologationOverview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => z.object({ days: z.number().int().min(1).max(90).default(14) }).parse(raw ?? {}))
  .handler(async ({ data, context }): Promise<HomologationOverview> => {
    await assertAdmin(context.supabase, context.userId);
    const supabase = context.supabase;
    const since = new Date(Date.now() - data.days * 86_400_000).toISOString();

    const [cohortsRes, outcomesRes, feedbackRes, actionsRes, cfgRes] = await Promise.all([
      supabase.from("ai_homologation_cohorts").select("*").gte("entered_at", since),
      supabase.from("ai_suggestion_outcomes").select("outcome, edit_magnitude, reject_reason, section_key, user_id").gte("created_at", since),
      supabase.from("ai_homologation_feedback").select("trigger, rating").gte("created_at", since),
      supabase.from("ai_actions_log").select("action_type, success, error_type, latency_ms, cost_estimate, user_id").gte("created_at", since),
      supabase.from("ai_homologation_config").select("key, value"),
    ]);

    const cohorts = (cohortsRes.data ?? []) as any[];
    const outcomes = (outcomesRes.data ?? []) as any[];
    const feedback = (feedbackRes.data ?? []) as any[];
    const actions = (actionsRes.data ?? []) as any[];
    const cfg = Object.fromEntries(((cfgRes.data ?? []) as any[]).map((r) => [r.key, r.value ?? {}]));

    const byGroup: Record<CohortGroup, number> = { internal: 0, assisted: 0, expanded: 0, gradual: 0, control: 0 };
    let finished = 0, active = 0, paused = 0;
    const eligibleUsers = new Set<string>();
    for (const c of cohorts) {
      byGroup[c.cohort_group as CohortGroup] = (byGroup[c.cohort_group as CohortGroup] ?? 0) + 1;
      if (c.status === "finished") finished++;
      else if (c.status === "paused") paused++;
      else active++;
      if (c.cohort_group !== "control") eligibleUsers.add(c.user_id);
    }

    const openedUsers = new Set(actions.filter((a) => a.action_type === "start_conversation" || a.action_type === "open").map((a) => a.user_id));
    const messagedUsers = new Set(actions.filter((a) => a.action_type === "send_message").map((a) => a.user_id));
    const appliedUsers = new Set(outcomes.filter((o) => o.outcome === "applied" || o.outcome === "applied_after_edit").map((o) => o.user_id));

    const applied = outcomes.filter((o) => o.outcome === "applied").length;
    const appliedAfterEdit = outcomes.filter((o) => o.outcome === "applied_after_edit").length;
    const rejected = outcomes.filter((o) => o.outcome === "rejected").length;
    const regenerated = outcomes.filter((o) => o.outcome === "regenerated").length;
    const total = applied + appliedAfterEdit + rejected;
    const applyRate = total > 0 ? (applied + appliedAfterEdit) / total : 0;
    const rejectRate = total > 0 ? rejected / total : 0;

    const positive = feedback.filter((f) => ["very_useful","useful","yes"].includes(f.rating)).length;
    const usefulFeedbackRate = feedback.length > 0 ? positive / feedback.length : 0;

    let deltaSum = 0, deltaN = 0, pendSum = 0, pendN = 0;
    for (const c of cohorts) {
      if (c.completeness_start != null && c.completeness_end != null) {
        deltaSum += Number(c.completeness_end) - Number(c.completeness_start); deltaN++;
      }
      if (c.pending_start != null && c.pending_end != null) {
        pendSum += Number(c.pending_end) - Number(c.pending_start); pendN++;
      }
    }

    const llmErrors = actions.filter((a) => a.success === false).length;
    const llmTimeouts = actions.filter((a) => a.error_type === "timeout").length;
    const costUsd = actions.reduce((acc, a) => acc + Number(a.cost_estimate ?? 0), 0);
    const latencies = actions.map((a) => Number(a.latency_ms ?? 0)).filter((n) => n > 0).sort((a, b) => a - b);
    const p95 = latencies.length ? latencies[Math.min(latencies.length - 1, Math.floor(latencies.length * 0.95))] : null;

    const success = (cfg["success_criteria"] ?? {}) as Record<string, number>;
    const pause = (cfg["pause_thresholds"] ?? {}) as Record<string, number>;

    const errorRate = actions.length ? llmErrors / actions.length : 0;
    const timeoutRate = actions.length ? llmTimeouts / actions.length : 0;

    const missing: string[] = [];
    if (applyRate < (success.apply_rate_min ?? 0.35)) missing.push(`taxa de aplicação (${(applyRate * 100).toFixed(0)}%)`);
    if (rejectRate > (success.reject_rate_max ?? 0.35)) missing.push(`rejeição alta (${(rejectRate * 100).toFixed(0)}%)`);
    if (usefulFeedbackRate < (success.useful_feedback_min ?? 0.6) && feedback.length >= 5) missing.push("feedback útil abaixo do alvo");
    if (errorRate > (success.error_rate_max ?? 0.03)) missing.push(`erros (${(errorRate * 100).toFixed(1)}%)`);
    if (timeoutRate > (success.timeout_rate_max ?? 0.02)) missing.push(`timeouts (${(timeoutRate * 100).toFixed(1)}%)`);
    if (p95 && p95 > (success.p95_latency_ms_max ?? 12000)) missing.push(`latência p95 alta`);

    let state: HomologationOverview["decision"]["state"];
    const anyPause =
      errorRate > (pause.error_rate_max ?? 0.08) ||
      timeoutRate > (pause.timeout_rate_max ?? 0.06) ||
      rejectRate > (pause.reject_rate_max ?? 0.55);
    if (cohorts.length === 0) state = "not_started";
    else if (anyPause) state = "blocked";
    else if (missing.length === 0 && cohorts.length >= 5 && finished >= 3) state = "ready_for_expansion";
    else if (missing.length === 0) state = "healthy";
    else if (missing.length >= 3) state = "attention";
    else state = "in_homologation";

    // Reasons + feedback per trigger + per section (agrupados).
    const rejectMap = new Map<string, number>();
    for (const o of outcomes) if (o.reject_reason) rejectMap.set(o.reject_reason, (rejectMap.get(o.reject_reason) ?? 0) + 1);
    const rejectReasons = Array.from(rejectMap.entries()).map(([reason, count]) => ({ reason, count }))
      .sort((a, b) => b.count - a.count);

    const fbMap = new Map<string, { useful: number; notUseful: number }>();
    for (const f of feedback) {
      const b = fbMap.get(f.trigger) ?? { useful: 0, notUseful: 0 };
      if (["very_useful","useful","yes"].includes(f.rating)) b.useful++; else b.notUseful++;
      fbMap.set(f.trigger, b);
    }

    const secMap = new Map<string, { suggestions: number; applied: number; rejected: number }>();
    for (const o of outcomes) {
      const k = o.section_key ?? "sem_secao";
      const b = secMap.get(k) ?? { suggestions: 0, applied: 0, rejected: 0 };
      b.suggestions++;
      if (o.outcome === "applied" || o.outcome === "applied_after_edit") b.applied++;
      if (o.outcome === "rejected") b.rejected++;
      secMap.set(k, b);
    }

    return {
      windowDays: data.days,
      cohorts: { total: cohorts.length, byGroup, finished, active, paused },
      adoption: {
        eligibleUsers: eligibleUsers.size,
        openedUsers: openedUsers.size,
        messagedUsers: messagedUsers.size,
        appliedUsers: appliedUsers.size,
      },
      utility: { applied, appliedAfterEdit, rejected, regenerated, applyRate, rejectRate, usefulFeedbackRate },
      outcome: {
        avgCompletenessDelta: deltaN > 0 ? deltaSum / deltaN : null,
        avgPendingDelta: pendN > 0 ? pendSum / pendN : null,
        finishedCohorts: finished,
      },
      operation: { llmErrors, llmTimeouts, costUsd, p95LatencyMs: p95 },
      criteria: { success, pause },
      decision: { state, missing },
      rejectReasons,
      feedbackByTrigger: Array.from(fbMap.entries()).map(([trigger, v]) => ({ trigger, ...v })),
      bySection: Array.from(secMap.entries()).map(([section, v]) => ({ section, ...v }))
        .sort((a, b) => b.suggestions - a.suggestions),
    };
  });

// ---------- helpers públicos client-safe ----------

/** Calcula dimensão de edição comparando tamanhos (sem persistir texto). */
export function estimateEditMagnitude(original: string, edited: string): "small" | "moderate" | "large" {
  const o = original.trim(), e = edited.trim();
  if (o.length === 0) return e.length > 40 ? "large" : "moderate";
  const diff = Math.abs(o.length - e.length) / Math.max(1, o.length);
  if (diff < 0.15) return "small";
  if (diff < 0.5) return "moderate";
  return "large";
}
